import React from "react";
import { Route, Switch } from "react-router-dom";
import { IntercomConversationApi, OrganizationPartnerInfoApi } from "../../../../../../services/Api/Admin/adminTypes";
import { OrganizationApi, OrganizationProductPerCategoryApi, UserRole } from "../../../../../../services/Api/types";
import { OrganizationPreviewRequests } from "../services/types";
import ApplicationsTabPreview from "./tabs/ApplicationsTabPreview";
import FeaturesTabPreview from "./tabs/FeaturesTabPreview";
import InvoicesTabPreview from "./tabs/InvoicesTabPreview";
import PartnersTabPreview from "./tabs/PartnersTabPreview";
import ProductsTabPreview from "./tabs/ProductsTabPreview";
import SubscriptionsTabPreview from "./tabs/SubscriptionsTabPreview";
import TerminalsTabPreview from "./tabs/TerminalsTabPreview";
import UsersTabPreview from "./tabs/UsersTabPreview";
import SupportTabPreview from "./tabs/support/SupportTabPreview";

interface Props {
	areAllowed: (contexts: Record<string, UserRole[]>) => boolean;
	url: string;
	requests: OrganizationPreviewRequests;
	organization: OrganizationApi;
	refresh: () => Promise<void>;
	partners: OrganizationPartnerInfoApi[];
	conversations: IntercomConversationApi[];
	loading: boolean;
	products: OrganizationProductPerCategoryApi[];
	loadingProducts: boolean;
	context: string;
}

const OrganizationPreviewRoutes = ({
	areAllowed,
	url,
	organization,
	requests,
	refresh,
	partners,
	conversations,
	loading,
	loadingProducts,
	products,
	context,
}: Props) => {
	return (
		<Switch>
			{areAllowed({ ADMIN: [], PARTNER: [], SUPPORT: [] }) && (
				<Route
					exact
					path={`${url}/terminals`}
					render={() => <TerminalsTabPreview requests={requests} organizationId={organization.id} />}
				/>
			)}
			{areAllowed({ ADMIN: [], SUPPORT: [] }) && (
				<Route exact path={`${url}/partners`} render={() => <PartnersTabPreview partners={partners} />} />
			)}
			{areAllowed({ ADMIN: [], PARTNER: [], SUPPORT: [] }) && (
				<Route
					exact
					path={`${url}/subscriptions`}
					render={() => (
						<SubscriptionsTabPreview
							organization={organization}
							onRefresh={refresh}
							requests={requests}
							editable={areAllowed({ ADMIN: [], PARTNER: [] })}
						/>
					)}
				/>
			)}
			<Route
				exact
				path={`${url}/users`}
				render={() => (
					<UsersTabPreview
						requests={requests}
						organizationId={organization.id}
						applications={organization.applications}
					/>
				)}
			/>
			{areAllowed({
				ADMIN: [],
				PARTNER: [UserRole.ADMIN, UserRole.OFFICE, UserRole.SALE, UserRole.SUPPORT],
				SUPPORT: [],
			}) && (
				<Route
					exact
					path={`${url}/support`}
					render={() => <SupportTabPreview loading={loading} conversations={conversations} />}
				/>
			)}
			{areAllowed({ ADMIN: [], PARTNER: [UserRole.ADMIN, UserRole.OFFICE, UserRole.SALE, UserRole.SUPPORT] }) && (
				<Route
					exact
					path={`${url}/products`}
					render={() => (
						<ProductsTabPreview organization={organization} products={products} loading={loadingProducts} />
					)}
				/>
			)}
			{areAllowed({ ADMIN: [], PARTNER: [], SUPPORT: [] }) && (
				<Route
					exact
					path={`${url}/features`}
					render={() => (
						<FeaturesTabPreview
							areAllowed={areAllowed}
							organization={organization}
							requests={requests}
							context={context}
						/>
					)}
				/>
			)}
			{areAllowed({
				ADMIN: [],
				PARTNER: [],
				SUPPORT: [UserRole.ADMIN, UserRole.OFFICE, UserRole.SALE, UserRole.SUPPORT],
			}) && (
				<Route
					exact
					path={`${url}/invoices`}
					render={() => <InvoicesTabPreview organization={organization} requests={requests} />}
				/>
			)}
			<>
				<Route exact path={`${url}`} render={() => <ApplicationsTabPreview organization={organization} />} />
			</>
		</Switch>
	);
};

export default OrganizationPreviewRoutes;
